import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, Trophy, Flame, Clock, TrendingUp, Calculator,
  ChevronRight, Crown, Star, Target, Zap, Award, Settings,
  CreditCard, User, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACHIEVEMENT_INFO = {
  first_tutorial: { name: "First Steps", icon: Star, description: "Completed your first tutorial" },
  five_tutorials: { name: "Quick Learner", icon: Zap, description: "Completed 5 tutorials" },
  ten_tutorials: { name: "Dedicated Student", icon: Award, description: "Completed 10 tutorials" },
  week_streak: { name: "Week Warrior", icon: Flame, description: "7-day learning streak" }
};

const Dashboard = () => {
  const { user, tier, refreshUser } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress`);
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(`${API}/customer-portal`, {
        return_url: window.location.href
      });
      window.location.href = response.data.url;
    } catch (error) {
      alert(error.response?.data?.detail || 'Unable to open subscription management');
      setPortalLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0 min';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes} min`;
  };

  const getTierBadge = () => {
    switch (tier) {
      case 'premium':
        return { name: 'Premium', color: 'gold', icon: Crown };
      case 'pro':
        return { name: 'Pro', color: 'purple', icon: Star };
      default:
        return { name: 'Free', color: 'gray', icon: User };
    }
  };

  const tierBadge = getTierBadge();
  const TierIcon = tierBadge.icon;

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading your progress...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page" data-testid="dashboard-page">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="user-info">
            <div className="user-avatar">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <h1>Welcome back, {user?.name?.split(' ')[0] || 'Trader'}!</h1>
              <div className={`tier-badge ${tierBadge.color}`}>
                <TierIcon size={14} />
                {tierBadge.name} Member
              </div>
            </div>
          </div>
          <div className="header-actions">
            {tier !== 'free' && (
              <button 
                className="btn btn-outline"
                onClick={openCustomerPortal}
                disabled={portalLoading}
                data-testid="manage-subscription-btn"
              >
                <CreditCard size={18} />
                {portalLoading ? 'Loading...' : 'Manage Subscription'}
              </button>
            )}
            {tier === 'free' && (
              <Link to="/pricing" className="btn btn-primary" data-testid="upgrade-btn">
                <Crown size={18} />
                Upgrade Plan
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <motion.div 
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="stat-icon tutorials">
            <BookOpen size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{progress?.total_tutorials_completed || 0}</span>
            <span className="stat-label">Tutorials Completed</span>
          </div>
        </motion.div>

        <motion.div 
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="stat-icon streak">
            <Flame size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{progress?.streak_days || 0}</span>
            <span className="stat-label">Day Streak</span>
          </div>
        </motion.div>

        <motion.div 
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="stat-icon time">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatTime(progress?.total_time_spent_seconds)}</span>
            <span className="stat-label">Learning Time</span>
          </div>
        </motion.div>

        <motion.div 
          className="stat-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="stat-icon trades">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{progress?.paper_trades_count || 0}</span>
            <span className="stat-label">Paper Trades</span>
          </div>
        </motion.div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Achievements Section */}
        <motion.div 
          className="dashboard-card achievements-card"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="card-header">
            <Trophy size={20} />
            <h2>Achievements</h2>
          </div>
          <div className="achievements-list">
            {Object.entries(ACHIEVEMENT_INFO).map(([key, info]) => {
              const isUnlocked = progress?.achievements?.includes(key);
              const Icon = info.icon;
              return (
                <div 
                  key={key} 
                  className={`achievement-item ${isUnlocked ? 'unlocked' : 'locked'}`}
                >
                  <div className="achievement-icon">
                    <Icon size={20} />
                  </div>
                  <div className="achievement-info">
                    <span className="achievement-name">{info.name}</span>
                    <span className="achievement-desc">{info.description}</span>
                  </div>
                  {isUnlocked && <CheckCircle size={18} className="check-icon" />}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div 
          className="dashboard-card activity-card"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="card-header">
            <BookOpen size={20} />
            <h2>Completed Tutorials</h2>
          </div>
          <div className="tutorials-list">
            {progress?.tutorials_completed?.length > 0 ? (
              progress.tutorials_completed.slice(-5).reverse().map((tutorial, idx) => (
                <div key={idx} className="tutorial-item">
                  <div className={`tutorial-level ${tutorial.level}`}>
                    {tutorial.level?.charAt(0).toUpperCase()}
                  </div>
                  <div className="tutorial-info">
                    <span className="tutorial-title">{tutorial.title}</span>
                    <span className="tutorial-meta">
                      {tutorial.level} • {formatTime(tutorial.time_spent_seconds)}
                    </span>
                  </div>
                  <CheckCircle size={16} className="complete-icon" />
                </div>
              ))
            ) : (
              <div className="empty-state">
                <BookOpen size={32} />
                <p>No tutorials completed yet</p>
                <Link to="/learn" className="btn btn-primary btn-sm">
                  Start Learning
                </Link>
              </div>
            )}
          </div>
          {progress?.tutorials_completed?.length > 0 && (
            <Link to="/learn" className="view-all-link">
              View All Tutorials <ChevronRight size={16} />
            </Link>
          )}
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Continue Your Journey</h2>
        <div className="actions-grid">
          <Link to="/learn" className="action-card">
            <div className="action-icon learn">
              <BookOpen size={24} />
            </div>
            <span>Learning Hub</span>
          </Link>
          <Link to="/trade" className="action-card">
            <div className="action-icon trade">
              <TrendingUp size={24} />
            </div>
            <span>Paper Trading</span>
          </Link>
          <Link to="/calculators" className="action-card">
            <div className="action-icon calc">
              <Calculator size={24} />
            </div>
            <span>Calculators</span>
          </Link>
          <Link to="/markets" className="action-card">
            <div className="action-icon market">
              <Target size={24} />
            </div>
            <span>Markets</span>
          </Link>
        </div>
      </div>

      <style>{`
        .dashboard-page {
          padding: 24px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .dashboard-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 16px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Header */
        .dashboard-header {
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .user-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--primary) 0%, #00d4aa 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
          color: #0a0a0f;
        }

        .user-details h1 {
          font-size: 1.5rem;
          margin-bottom: 8px;
        }

        .tier-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .tier-badge.gold {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 180, 0, 0.2) 100%);
          color: #ffd700;
        }

        .tier-badge.purple {
          background: rgba(138, 43, 226, 0.2);
          color: #ba68c8;
        }

        .tier-badge.gray {
          background: var(--bg-subtle);
          color: var(--text-muted);
        }

        .header-actions .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          cursor: pointer;
        }

        .btn-outline {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text-secondary);
        }

        .btn-outline:hover:not(:disabled) {
          border-color: var(--primary);
          color: var(--primary);
        }

        .btn-primary {
          background: var(--primary);
          border: none;
          color: #0a0a0f;
        }

        .btn-primary:hover {
          background: var(--primary-light);
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stat-icon.tutorials {
          background: rgba(0, 255, 148, 0.1);
          color: var(--primary);
        }

        .stat-icon.streak {
          background: rgba(255, 100, 50, 0.1);
          color: #ff6432;
        }

        .stat-icon.time {
          background: rgba(100, 150, 255, 0.1);
          color: #6496ff;
        }

        .stat-icon.trades {
          background: rgba(180, 100, 255, 0.1);
          color: #b464ff;
        }

        .stat-content {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          line-height: 1.2;
        }

        .stat-label {
          font-size: 13px;
          color: var(--text-muted);
        }

        /* Dashboard Grid */
        .dashboard-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          margin-bottom: 32px;
        }

        .dashboard-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 24px;
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
          color: var(--primary);
        }

        .card-header h2 {
          font-size: 1.125rem;
          color: var(--text-primary);
        }

        /* Achievements */
        .achievements-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .achievement-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-radius: 10px;
          background: var(--bg-subtle);
          transition: all 0.2s;
        }

        .achievement-item.locked {
          opacity: 0.5;
        }

        .achievement-item.unlocked {
          background: rgba(0, 255, 148, 0.08);
          border: 1px solid rgba(0, 255, 148, 0.2);
        }

        .achievement-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: var(--bg-paper);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
        }

        .achievement-item.unlocked .achievement-icon {
          background: var(--primary);
          color: #0a0a0f;
        }

        .achievement-info {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .achievement-name {
          font-weight: 600;
          font-size: 14px;
        }

        .achievement-desc {
          font-size: 12px;
          color: var(--text-muted);
        }

        .check-icon {
          color: var(--primary);
        }

        /* Tutorials List */
        .tutorials-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .tutorial-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-radius: 10px;
          background: var(--bg-subtle);
        }

        .tutorial-level {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
        }

        .tutorial-level.beginner {
          background: rgba(0, 255, 148, 0.2);
          color: var(--primary);
        }

        .tutorial-level.intermediate {
          background: rgba(255, 200, 50, 0.2);
          color: #ffc832;
        }

        .tutorial-level.advanced {
          background: rgba(255, 100, 50, 0.2);
          color: #ff6432;
        }

        .tutorial-info {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .tutorial-title {
          font-weight: 500;
          font-size: 14px;
        }

        .tutorial-meta {
          font-size: 12px;
          color: var(--text-muted);
          text-transform: capitalize;
        }

        .complete-icon {
          color: var(--primary);
        }

        .empty-state {
          text-align: center;
          padding: 32px;
          color: var(--text-muted);
        }

        .empty-state svg {
          margin-bottom: 12px;
          opacity: 0.5;
        }

        .empty-state p {
          margin-bottom: 16px;
        }

        .btn-sm {
          padding: 8px 16px;
          font-size: 13px;
        }

        .view-all-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--border);
          color: var(--primary);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
        }

        .view-all-link:hover {
          text-decoration: underline;
        }

        /* Quick Actions */
        .quick-actions {
          margin-top: 32px;
        }

        .quick-actions h2 {
          font-size: 1.25rem;
          margin-bottom: 20px;
        }

        .actions-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .action-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          color: var(--text-primary);
          transition: all 0.2s;
        }

        .action-card:hover {
          border-color: var(--primary);
          transform: translateY(-2px);
        }

        .action-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .action-icon.learn {
          background: rgba(0, 255, 148, 0.1);
          color: var(--primary);
        }

        .action-icon.trade {
          background: rgba(100, 150, 255, 0.1);
          color: #6496ff;
        }

        .action-icon.calc {
          background: rgba(180, 100, 255, 0.1);
          color: #b464ff;
        }

        .action-icon.market {
          background: rgba(255, 200, 50, 0.1);
          color: #ffc832;
        }

        .action-card span {
          font-size: 14px;
          font-weight: 500;
        }

        /* Responsive */
        @media (max-width: 900px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .dashboard-grid {
            grid-template-columns: 1fr;
          }

          .actions-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 600px) {
          .dashboard-page {
            padding: 16px;
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .actions-grid {
            grid-template-columns: 1fr 1fr;
          }

          .user-avatar {
            width: 48px;
            height: 48px;
            font-size: 1.25rem;
          }

          .user-details h1 {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
